import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import * as styles from './InfiniteScroll.module.css';

type InfiniteScrollProps = JSX.IntrinsicElements['div'] & {
  gap?: number;
  duration?: number;
  direction?: 'left' | 'right';
  pauseOnHover?: boolean;
};

export const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  gap = 16,
  duration = 10000,
  direction = 'left',
  pauseOnHover = true,
  children,
  className,
  ...props
}) => {
  const [enableScrolling, setEnableScrolling] = useState<boolean>(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const groupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      if (groupRef.current) {
        const parentWidth = entries[0].contentRect.width;
        const groupWidth = groupRef.current.offsetWidth;
        setEnableScrolling(Math.round(groupWidth) > Math.round(parentWidth));
      }
    });

    resizeObserver.observe(parentRef.current!);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div
      {...props}
      ref={parentRef}
      className={clsx(
        styles.parent,
        enableScrolling && styles.enableScrolling,
        direction === 'right' && styles.reverse,
        pauseOnHover && styles.pauseOnHover,
        className,
      )}
      style={{ '--gap': `${gap}px`, '--duration': `${duration}ms` } as React.CSSProperties}
    >
      <div className={styles.track}>
        <div ref={groupRef} className={styles.group}>
          {children}
        </div>
        {enableScrolling && <div className={styles.group}>{children}</div>}
      </div>
    </div>
  );
};
